/**
 * Hook for managing report filter state (HRO, KPI, Organization)
 *
 * Extracted from useAdminReports.js to reduce complexity.
 * Handles filter dropdowns, values, and organization access.
 */
import { useState, useCallback, useEffect } from 'react';

import { getOrganizationAccess } from '../util/storage';
import { DEFAULT_HROS } from '../util/reports';

/**
 * Custom hook for report filtering state
 *
 * @param {string} organization - The current organization
 * @param {string} filterOrg - Optional filter organization from route
 * @returns {Object} Filter state and handlers
 */
export default function useReportFilters(organization, filterOrg = null) {
  // HRO filter state
  const [HROFilterOpen, setHROFilterOpen] = useState(false);
  const [HROFilterValue, setHROFilterValue] = useState(null);
  const [HROFilterValueShouldReset, setHROFilterValueShouldReset] = useState(false);
  const [HROFilter, setHROFilter] = useState(
    DEFAULT_HROS.map((x) => ({ label: x, value: x }))
  );

  // KPI filter state
  const [KPIFilterOpen, setKPIFilterOpen] = useState(false);
  const [KPIFilterValue, setKPIFilterValue] = useState([]);
  const [KPIFilterValueShouldReset, setKPIFilterValueShouldReset] = useState(false);
  const [KPIFilter, setKPIFilter] = useState([]);

  // Organization filter state — initialize with current org to avoid
  // a [] → [org] transition that would re-create fetchTicketCounts on mount
  const [OrgFilterOpen, setOrgFilterOpen] = useState(false);
  const [OrgFilterValue, setOrgFilterValue] = useState([filterOrg ?? organization]);
  const [OrgFilterValueShouldReset, setOrgFilterValueShouldReset] = useState(false);
  const [validOrgs, setValidOrgs] = useState([]);
  const [OrgFilter, setOrgFilter] = useState(
    filterOrg ? [filterOrg, organization] : [organization]
  );
  const [loadingOrg, setLoadingOrg] = useState(true);

  /**
   * Update valid organizations from storage
   */
  const updateValidOrgs = useCallback(async () => {
    let organization_access = await getOrganizationAccess();
    organization_access = organization_access.length ? organization_access : [organization];
    setValidOrgs(organization_access);
    setOrgFilter(organization_access.map((x) => ({ label: x, value: x })));
  }, [organization]);

  /**
   * Check if HRO filter should trigger refresh
   * Returns true if filter changed and dropdown was closed
   */
  const checkHROFilterChanged = useCallback(() => {
    if (!HROFilterOpen) {
      const shouldTrigger = (HROFilterValue?.length > 0) || HROFilterValueShouldReset;
      setHROFilterValueShouldReset(HROFilterValue?.length > 0);
      return shouldTrigger;
    }
    return false;
  }, [HROFilterOpen, HROFilterValue, HROFilterValueShouldReset]);

  /**
   * Check if KPI filter should trigger refresh
   * Returns true if filter changed and dropdown was closed
   */
  const checkKPIFilterChanged = useCallback(() => {
    if (!KPIFilterOpen) {
      const shouldTrigger = (KPIFilterValue?.length > 0) || KPIFilterValueShouldReset;
      setKPIFilterValueShouldReset(KPIFilterValue?.length > 0);
      return shouldTrigger;
    }
    return false;
  }, [KPIFilterOpen, KPIFilterValue, KPIFilterValueShouldReset]);

  /**
   * Check if Org filter should trigger refresh
   * Returns true if filter changed and dropdown was closed
   */
  const checkOrgFilterChanged = useCallback(() => {
    if (!OrgFilterOpen) {
      const shouldTrigger = (OrgFilterValue?.length > 0) || OrgFilterValueShouldReset;
      setOrgFilterValueShouldReset(OrgFilterValue?.length > 0);
      return shouldTrigger;
    }
    return false;
  }, [OrgFilterOpen, OrgFilterValue, OrgFilterValueShouldReset]);

  /**
   * Reset all filters to default state
   */
  const resetFilters = useCallback(() => {
    setHROFilterValue(null);
    setKPIFilterValue([]);
    setOrgFilterValue([filterOrg ?? organization]);
  }, [filterOrg, organization]);

  // Initialize valid orgs on mount
  useEffect(() => {
    updateValidOrgs();
  }, [updateValidOrgs]);

  // Update loading state when orgs are loaded
  useEffect(() => {
    setLoadingOrg(validOrgs.length === 0);
  }, [validOrgs.length]);

  // Initialize OrgFilterValue once on mount
  useEffect(() => {
    setOrgFilterValue([filterOrg ?? organization]);
  }, [filterOrg, organization]);

  return {
    // HRO filter
    HROFilterOpen,
    setHROFilterOpen,
    HROFilterValue,
    setHROFilterValue,
    HROFilter,
    setHROFilter,

    // KPI filter
    KPIFilterOpen,
    setKPIFilterOpen,
    KPIFilterValue,
    setKPIFilterValue,
    KPIFilter,
    setKPIFilter,

    // Organization filter
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,
    loadingOrg,
    validOrgs,

    // Actions
    updateValidOrgs,
    resetFilters,
    checkHROFilterChanged,
    checkKPIFilterChanged,
    checkOrgFilterChanged,
  };
}
